import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MdClose, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { MediaType } from '../../../codegen/graphql';
import cx from 'classnames';
import type { Image } from './ImageProvider';

type DisplayRatio = 'original' | '1:1' | '3:4';

/** Smallest float canvas with target aspect that fits the image (object-fit: contain). */
function computeContainCanvasSize(
  origWidth: number,
  origHeight: number,
  targetAspectRatio: number
): { width: number; height: number } {
  const ri = origWidth / origHeight;
  if (ri >= targetAspectRatio) {
    return { width: origWidth, height: origWidth / targetAspectRatio };
  }
  return { width: origHeight * targetAspectRatio, height: origHeight };
}

/**
 * Turn float canvas into integer width/height with an exact ratio by adding (or trimming) whole pixels.
 */
function snapFloatCanvasToIntegerRatioPixels(
  wf: number,
  hf: number,
  ratio: '1:1' | '3:4'
): { width: number; height: number } {
  if (ratio === '1:1') {
    const side = Math.max(1, Math.ceil(Math.max(wf, hf)));
    return { width: side, height: side };
  }
  const k = Math.max(1, Math.ceil(wf / 3), Math.ceil(hf / 4));
  return { width: 3 * k, height: 4 * k };
}

interface LightboxCanvasImageProps {
  url: string;
  ratio: '1:1' | '3:4';
  label: string;
}

/** Integer CSS size (exact ratio) that fits inside maxW × maxH without fractional aspect. */
function fitDisplaySizeToViewport(
  bitmapW: number,
  bitmapH: number,
  ratio: '1:1' | '3:4',
  maxW: number,
  maxH: number
): { cssW: number; cssH: number } {
  const scale = Math.min(maxW / bitmapW, maxH / bitmapH, 1);
  if (ratio === '1:1') {
    const s = Math.max(1, Math.floor(Math.min(bitmapW, bitmapH) * scale));
    return { cssW: s, cssH: s };
  }
  const k = Math.max(1, Math.floor((bitmapH * scale) / 4));
  return { cssW: 3 * k, cssH: 4 * k };
}

function LightboxCanvasImage({ url, ratio, label }: LightboxCanvasImageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [bitmapSize, setBitmapSize] = useState<{ w: number; h: number } | null>(null);
  const [displaySize, setDisplaySize] = useState<{ cssW: number; cssH: number } | null>(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el || !bitmapSize) return;

    const update = () => {
      const r = el.getBoundingClientRect();
      const maxW = Math.max(1, r.width || window.innerWidth * 0.9);
      const maxH = Math.max(1, Math.min(window.innerHeight * 0.9, r.height || window.innerHeight * 0.9));
      setDisplaySize(fitDisplaySizeToViewport(bitmapSize.w, bitmapSize.h, ratio, maxW, maxH));
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [bitmapSize, ratio]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setBitmapSize(null);
    setDisplaySize(null);

    const img = new Image();
    let cancelled = false;

    img.onload = () => {
      if (cancelled) return;
      const wo = img.naturalWidth;
      const ho = img.naturalHeight;
      if (wo <= 0 || ho <= 0) return;

      const targetAspect = ratio === '1:1' ? 1 : 3 / 4;
      const { width: wf, height: hf } = computeContainCanvasSize(wo, ho, targetAspect);
      const { width: cw, height: ch } = snapFloatCanvasToIntegerRatioPixels(wf, hf, ratio);

      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, cw, ch);
      const scale = Math.min(cw / wo, ch / ho);
      const dw = wo * scale;
      const dh = ho * scale;
      const dx = (cw - dw) / 2;
      const dy = (ch - dh) / 2;
      ctx.drawImage(img, 0, 0, wo, ho, dx, dy, dw, dh);

      setBitmapSize({ w: cw, h: ch });
    };

    img.src = url;

    return () => {
      cancelled = true;
    };
  }, [url, ratio]);

  return (
    <div
      ref={wrapRef}
      className="flex min-h-0 w-full max-w-full flex-1 items-center justify-center bg-white"
    >
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={label}
        className="block shrink-0"
        style={
          displaySize
            ? { width: displaySize.cssW, height: displaySize.cssH }
            : { visibility: 'hidden' }
        }
      />
    </div>
  );
}

function hasImageDimensions(media: Image): boolean {
  return (
    media.type === MediaType.Image &&
    typeof media.width === 'number' &&
    typeof media.height === 'number' &&
    media.width > 0 &&
    media.height > 0
  );
}

interface LightboxProps {
  medias: Image[];
  initialIndex?: number;
  onClose: () => void;
  className?: string;
}

export function Lightbox({ medias, initialIndex, onClose, className }: LightboxProps) {
  const lightboxContainerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);
  const [displayRatioByMediaId, setDisplayRatioByMediaId] = useState<Record<string, DisplayRatio>>({});
  const currentMedia = medias[currentIndex];
  const currentRatio = displayRatioByMediaId[currentMedia.id] ?? 'original';

  const handleNext = () => {
    setCurrentIndex(prevIndex => Math.min(prevIndex + 1, medias.length - 1));
  };

  const handlePrev = () => {
    setCurrentIndex(prevIndex => Math.max(prevIndex - 1, 0));
  };

  // Add keyboard event handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowRight':
          if (currentIndex < medias.length - 1) {
            handleNext();
          }
          break;
        case 'ArrowLeft':
          if (currentIndex > 0) {
            handlePrev();
          }
          break;
      }
    };

    lightboxContainerRef.current?.addEventListener('keydown', handleKeyDown);
    return () => lightboxContainerRef.current?.removeEventListener('keydown', handleKeyDown);
  }, [onClose, handleNext, handlePrev, currentIndex, medias.length]);

  useEffect(() => {
    lightboxContainerRef.current?.focus();
  }, []);

  return (
    <div
      ref={lightboxContainerRef}
      tabIndex={0}
      className={cx(
        'fixed inset-0 z-50 bg-black bg-opacity-90 flex flex-col items-center justify-center',
        className,
      )}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300"
      >
        <MdClose size={24} />
      </button>

      {hasImageDimensions(currentMedia) && (
        <div className="absolute top-4 left-1/2 z-10 -translate-x-1/2">
          <label className="sr-only" htmlFor="lightbox-display-ratio">
            Display ratio
          </label>
          <select
            id="lightbox-display-ratio"
            value={currentRatio}
            onChange={e =>
              setDisplayRatioByMediaId(prev => ({
                ...prev,
                [currentMedia.id]: e.target.value as DisplayRatio,
              }))
            }
            className="rounded border border-white/30 bg-black/70 px-2 py-1 text-xs text-white outline-none focus:ring-1 focus:ring-yellow-400"
          >
            <option value="original">Original</option>
            <option value="1:1">1:1</option>
            <option value="3:4">3:4</option>
          </select>
        </div>
      )}

      <button
        onClick={handlePrev}
        disabled={currentIndex === 0}
        className="absolute left-4 text-white hover:text-gray-300 disabled:opacity-50"
      >
        <MdChevronLeft size={48} />
      </button>

      <button
        onClick={handleNext}
        disabled={currentIndex === medias.length - 1}
        className="absolute right-4 text-white hover:text-gray-300 disabled:opacity-50"
      >
        <MdChevronRight size={48} />
      </button>

      <div className="grow flex justify-center">
        {currentMedia.type === MediaType.Video ? (
          <video
            src={currentMedia.url}
            className="max-w-full max-h-[90vh] object-contain"
            controls
            autoPlay
          />
        ) : currentRatio !== 'original' && hasImageDimensions(currentMedia) ? (
          <LightboxCanvasImage
            url={currentMedia.url}
            ratio={currentRatio}
            label={`Media ${currentIndex + 1}`}
          />
        ) : (
          <img
            src={currentMedia.url}
            alt={`Media ${currentIndex + 1}`}
            className="max-w-full max-h-[90vh] object-contain"
          />
        )}
      </div>

      {/* Thumbnails Gallery */}
      <div className="grow-0 flex gap-2 px-4 py-2 bg-black bg-opacity-50 rounded-lg overflow-x-auto max-w-[90vw]">
        {medias.map((item, index) => (
          <button
            key={index}
            onClick={() => {
              setCurrentIndex(index);
              lightboxContainerRef.current?.focus();
            }}
            className={cx(
              'shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-all',
              currentIndex === index ? 'border-yellow-400 scale-105' : 'border-transparent opacity-70 hover:opacity-100'
            )}
          >
            {item.type === MediaType.Video ? (
              <video
                src={item.url}
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={item.url}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            )}
          </button>
        ))}
      </div>
    </div>
  );
} 